/**
 * 주소록에 담기 — 이 메일의 주소를 한꺼번에 (정본 **E1-4** 「고쳐 담는 표」, 2026-09-08).
 *
 * 메일에 있는 **모든 주소**(보낸사람 · 받는사람 · 참조 · 숨은참조)를 끌어와 한 줄씩 세운다.
 * 체크한 사람만 담기고, **이름 · 휴대폰은 그 자리에서 고칠 수 있다** — 담고 나서 목록으로
 * 다시 찾아 들어가 채우게 하면 아무도 안 채운다.
 *
 * ★★★ **담을 수 없는 줄도 세운다.** 나 자신 · 이미 담긴 사람은 잠그고 **이유를 배지로 적는다** —
 *    목록에서 빼면 「왜 다섯인데 셋만 나오지」가 되고, 그냥 두면 쌍둥이가 생긴다.
 * ★★ 「전체 선택」은 **못 고르는 줄을 담지 않는다**(담으면 머리 체크가 영영 «반쯤»에서 멈춘다).
 * ★ 메일주소는 못 고친다 — 고칠 수 있게 두면 그건 담기가 아니라 «다른 사람 새로 쓰기»다.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, ModalActions } from '../../components/shared/Modal';
import { Button } from '../../components/shared/Button';
import { Input } from '../../components/shared/Input';
import { Select } from '../../components/shared/Select';
import { Badge } from '../../components/shared/Badge';
import { Table, type TableColumn } from '../../components/shared/Table';
import { contactsApi, type ContactScope, type ContactTag } from '../../api/contacts';
import { tContacts, tContactsWith } from '../../lib/contacts-i18n';
import './ContactPickModal.css';

/** 이 메일에서 뽑아 온 주소 한 줄. */
export interface PickCandidate {
  email: string;
  name: string | null;
  /** 나 자신인가 — 담을 수 없다. */
  isMe?: boolean;
}

export interface ContactPickModalProps {
  candidates: PickCandidate[];
  /** 담을 곳에서 공유 주소록을 고를 수 있나 — 2026-09-15 부터 모두(서버 #1374 · 담기만). */
  canShare: boolean;
  onClose: () => void;
  onDone: (added: number) => void;
}

interface RowState {
  email: string;
  name: string;
  mobile: string;
  /**
   * 이 줄에 붙일 태그 (2026-09-08 · 시안 S4 대로 되살림).
   * ★ 머리에서 «전부에» 고르던 것을 **줄마다**로 옮겼다 — 한 메일에 거래처와 협력사가
   *   섞여 오는 것이 보통이라, 하나로 묶으면 담고 나서 다시 갈라야 한다.
   */
  tagId: string;
  /** 담을 수 없는 이유 — 없으면 고를 수 있다. */
  lockReason: 'ME' | 'ALREADY' | null;
}

export function ContactPickModal({ candidates, canShare, onClose, onDone }: ContactPickModalProps) {
  const [rows, setRows] = useState<RowState[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [scope, setScope] = useState<ContactScope>('PERSONAL');
  const [tags, setTags] = useState<ContactTag[]>([]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  /* 무엇이 이미 담겼는지 먼저 묻는다 — 안 물으면 쌍둥이를 만들어 놓고 서버가 거절한다. */
  useEffect(() => {
    let cancelled = false;
    const emails = candidates.map((c) => c.email);
    void contactsApi.lookup(emails)
      .then((res) => {
        if (cancelled) return;
        const next: RowState[] = candidates.map((c) => ({
          email: c.email,
          name: c.name ?? '',
          mobile: '',
          tagId: '',
          lockReason: c.isMe ? 'ME' : (res.contacts[c.email] ? 'ALREADY' : null),
        }));
        setRows(next);
        setSelected(next.filter((r) => r.lockReason === null).map((r) => r.email));
        setLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        /* 못 물어봤으면 «이미 담김»을 모른다 — 담기는 되고, 겹치는 줄은 서버가 건너뛴다. */
        const next: RowState[] = candidates.map((c) => ({
          email: c.email, name: c.name ?? '', mobile: '', tagId: '', lockReason: c.isMe ? 'ME' : null,
        }));
        setRows(next);
        setSelected(next.filter((r) => r.lockReason === null).map((r) => r.email));
        setLoaded(true);
      });
    return () => { cancelled = true; };
  }, [candidates]);

  useEffect(() => {
    void contactsApi.listTags().then(setTags).catch(() => setTags([]));
  }, []);

  const patchRow = useCallback((email: string, patch: Partial<RowState>) => {
    setRows((prev) => prev.map((r) => (r.email === email ? { ...r, ...patch } : r)));
  }, []);

  const submit = useCallback(async () => {
    const picked = rows.filter((r) => r.lockReason === null && selected.includes(r.email));
    if (picked.length === 0) return;
    setSaving(true);
    try {
      const res = await contactsApi.bulkCreate(
        picked.map((r) => ({
          email: r.email,
          name: r.name.trim() || null,
          mobile: r.mobile.trim() || null,
          tagIds: r.tagId ? [r.tagId] : undefined,
        })),
        scope,
      );
      onDone(res.added);
    } catch {
      setSaving(false);
    }
  }, [rows, selected, scope, onDone]);

  const columns: ReadonlyArray<TableColumn<RowState>> = useMemo(() => [
    {
      key: 'name',
      header: tContacts('contacts.col.name'),
      width: '190px',
      render: (row) => (
        row.lockReason
          ? <span className="cpm-dim">{row.name || tContacts('contacts.noname')}</span>
          : (
            <Input
              size="sm"
              value={row.name}
              placeholder={tContacts('contacts.pick.name_ph')}
              aria-label={`${row.email} ${tContacts('contacts.col.name')}`}
              fullWidth
              onChange={(e) => patchRow(row.email, { name: e.target.value })}
            />
          )
      ),
    },
    {
      key: 'email',
      header: tContacts('contacts.col.email'),
      /* 키가 되는 값이라 고칠 수 없다 — 글자로 둔다. */
      render: (row) => <span className="cpm-mono">{row.email}</span>,
    },
    {
      key: 'mobile',
      header: tContacts('contacts.pick.col_mobile'),
      width: '180px',
      render: (row) => (
        row.lockReason
          ? <span className="cpm-dim">—</span>
          : (
            <Input
              size="sm"
              value={row.mobile}
              placeholder={tContacts('contacts.pick.mobile_ph')}
              aria-label={`${row.email} ${tContacts('contacts.pick.col_mobile')}`}
              fullWidth
              onChange={(e) => patchRow(row.email, { mobile: e.target.value })}
            />
          )
      ),
    },
    {
      key: 'tag',
      header: tContacts('contacts.col.tags'),
      width: '170px',
      render: (row) => (
        row.lockReason || tags.length === 0
          ? <span className="cpm-dim">—</span>
          : (
            <Select
              value={row.tagId}
              size="sm"
              portal
              ariaLabel={`${row.email} ${tContacts('contacts.col.tags')}`}
              onChange={(v) => patchRow(row.email, { tagId: v })}
              options={[
                { value: '', label: tContacts('contacts.pick.tag_ph') },
                ...tags.map((t) => ({ value: t.id, label: t.name })),
              ]}
            />
          )
      ),
    },
    {
      key: 'why',
      header: '',
      width: '132px',
      /* ★ 잠근 줄의 «이유»가 사는 칸. 비워 두면 왜 못 고르는지 알 방법이 없다. */
      render: (row) => (
        row.lockReason === 'ME' ? <Badge size="sm">{tContacts('contacts.pick.me')}</Badge>
          : row.lockReason === 'ALREADY' ? <Badge variant="success" size="sm">{tContacts('contacts.pick.already')}</Badge>
            : null
      ),
    },
  ], [patchRow, tags]);

  const pickedCount = rows.filter((r) => r.lockReason === null && selected.includes(r.email)).length;

  return (
    <Modal
      title={tContacts('contacts.pick.title')}
      /*
       * ★ **lg(840×640)** 다 (2026-09-08 사용자 지적 — 「넓이와 높이가 작아서 내부가 안 보인다」).
       *   md(720)에서는 표 다섯 칸이 눌려 이름 입력이 120px 까지 좁아졌다(실측).
       *   이 팝업은 «표 + 그 자리에서 고치는 칸» 이라 다른 확인창보다 넓어야 한다.
       */
      size="lg"
      onClose={onClose}
      /*
       * ⚠⚠⚠ `bodyFlat` 을 쓰지 않는다 — 그것은 **`flex-direction: row`** 다
       *    (좌우로 나누는 레이아웃용). 이 팝업은 «머리 → 표 → 안내문» 세로 스택이라,
       *    켜 두면 셋이 **가로로 늘어서** 표가 눌리고 안내문이 옆에 붙는다(2026-09-08 실측).
       */
      footer={(
        <ModalActions>
          <span style={{ flex: 1 }} />
          <Button variant="secondary" onClick={onClose}>{tContacts('contacts.edit.cancel')}</Button>
          <Button
            variant="primary"
            loading={saving}
            disabled={pickedCount === 0}
            onClick={() => { void submit(); }}
          >
            {tContactsWith('contacts.pick.submit', { n: pickedCount })}
          </Button>
        </ModalActions>
      )}
    >
      <div className="cpm-head">
        <span className="cpm-head__n">{tContactsWith('contacts.pick.count', { n: candidates.length })}</span>
        {canShare && (
          <Select
            value={scope}
            portal
            ariaLabel={tContacts('contacts.edit.where')}
            onChange={(v) => setScope(v as ContactScope)}
            options={[
              { value: 'PERSONAL', label: tContacts('contacts.list.personal') },
              { value: 'SHARED', label: tContacts('contacts.list.shared') },
            ]}
          />
        )}
        <span className="cpm-head__sel">{tContactsWith('contacts.pick.selected', { n: pickedCount })}</span>
      </div>

      <Table
        caption={tContacts('contacts.pick.title')}
        columns={columns}
        rows={rows}
        rowKey={(row) => row.email}
        loading={!loaded}
        selectedKeys={selected}
        onSelectionChange={setSelected}
        rowSelectionLabel={(row) => row.name || row.email}
        rowSelectable={(row) => row.lockReason === null}
        rowClassName={(row) => (row.lockReason ? 'cpm-row--locked' : '')}
      />

      <p className="cpm-hint">{tContacts('contacts.pick.hint')}</p>
    </Modal>
  );
}
